import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useForm } from "react-hook-form";
import { User, Info } from "lucide-react";
import clsx from "clsx";
import { IPatientWaitingListData } from "@/api_framework/api_modals/Patients";
import { useOrgClinicProvidersAPI } from "@/app/hooks/api_hooks/org/useOrgClinicProvidersAPI";
import { useBookingRescheduleAndCancelAPI } from "@/app/hooks/api_hooks/bookings/useBookingRescheduleAndCancelationAPI";
import { IBookingRescheduleBody, IBookingUpdateModuleType } from "@/api_framework/api_modals/Bookings";
import { useMappedConstants } from "@/app/hooks/useMappedConstants";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ISlotsState } from "@/app_redux/reducers/slices/auth/slots_slice";

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  bookingDetails: IPatientWaitingListData;
  onSuccess?: () => void;
}

interface FormValues {
  doctor_id: string;
}

type ExtendedPWLBookingDetails = IPatientWaitingListData & {
  slot_id?: string;
  slot_details?: { slot_id?: string };
  configuration_id?: string | null;
  cart_details?: IBookingRescheduleBody["cart_details"];
  address?: string;
  address_id?: string;
  slot_type?: string;
  booking_type?: string | null;
  service_type_id?: string;
};

const normalizeDateTime = (value?: string) => value?.replace("T", " ") ?? "";

const constructChangeDoctorPayload = (
  booking: IPatientWaitingListData,
  newDoctorId: string,
  statusMapping: Record<string, string>,
): Partial<IBookingRescheduleBody> => {
  const extendedBooking = booking as ExtendedPWLBookingDetails;
  // "No Show" is computed by the PWL API for display only (15 min past end time);
  // the booking is still "Pending" in the DB. Echoing it back makes the backend
  // persist it, so send the underlying DB status instead.
  const displayStatus = booking.booking_status?.toLowerCase();
  const mappedStatus =
    displayStatus === "no show" ? "Pending" : (statusMapping[displayStatus] ?? booking.booking_status);
  const slotId = extendedBooking.slot_details?.slot_id ?? extendedBooking.slot_id;
  const serviceCartItemId = booking.subservice_id || booking.service_id;

  const cartDetails =
    extendedBooking.cart_details ??
    (serviceCartItemId
      ? {
          cart_items: [
            {
              type: "service",
              id: serviceCartItemId,
              qty: 1,
            },
          ],
        }
      : undefined);

  const payload: Partial<IBookingRescheduleBody> = {
    source_module: "appointment_module" as IBookingUpdateModuleType,
    booking_status: mappedStatus,
    booking_date: booking.booking_start_date,
    booking_start_date_time: normalizeDateTime(booking.booking_start_date_time),
    booking_end_date_time: normalizeDateTime(booking.booking_end_date_time),
    start_time: booking.booking_start_time,
    end_time: booking.booking_end_time,
    configuration_id: extendedBooking.configuration_id ?? (null as unknown as string),
    booking_type: extendedBooking.booking_type ?? null,
    slot_type: extendedBooking.slot_type ?? "free",
    clinic_details: {
      id: booking.clinic_details?.id || booking.clinic_id,
    },
    user_details: {
      id: booking.user_details?.id || booking.user_id,
    },
    pet_details: {
      ...(booking.pet_details || {}),
      id: booking.pet_details?.id || booking.pet_id,
    },
    staff_details: {
      id: newDoctorId,
    },
    service_details: {
      service_type_id:
        (booking.service_details as { service_type_id?: string })?.service_type_id || extendedBooking.service_type_id,
      service_id: booking.service_details?.id || booking.service_id,
      service_name: booking.service_details?.name || "",
      sub_service_id: booking.subservice_details?.id || booking.subservice_id || "",
      sub_service_name: booking.subservice_details?.name || "",
    },
    comments: booking.appointment_note || "",
  };

  if (slotId) {
    payload.slot_details = { slot_id: slotId };
  }

  if (cartDetails) {
    payload.cart_details = cartDetails;
  }

  if (extendedBooking.address) {
    payload.address = extendedBooking.address;
  }

  if (extendedBooking.address_id) {
    payload.address_id = extendedBooking.address_id;
  }

  return payload;
};

const ChangeDoctorModal = ({ open, setOpen, bookingDetails, onSuccess }: Props) => {
  const [loading, setLoading] = useState(false);
  const { appointmentBookingCommentEditStatusMapping } = useMappedConstants();
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("SLOTS", "id"));
  const { providers } = routeState.state as ISlotsState;

  const fetchPWLProviders = useOrgClinicProvidersAPI("SLOTS");
  const { execute: updateBooking } = useBookingRescheduleAndCancelAPI();

  const availableProviders = (providers || []).filter(
    (provider) => provider.staff_id !== (bookingDetails?.staff_id || bookingDetails?.staff_details?.id),
  );
  const providerList = useSelectMenuReducer(availableProviders, "name", "staff_id");

  const formHook = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      doctor_id: "",
    },
  });

  const {
    formState: { isValid },
  } = formHook;

  useEffect(() => {
    if (!open || !bookingDetails) return;

    formHook.reset({ doctor_id: "" });
    fetchPWLProviders({
    start_time: bookingDetails?.booking_start_time,
  end_time: bookingDetails?.booking_end_time,
      ROTADate: bookingDetails?.booking_start_date,
      clinicIds: [bookingDetails?.clinic_id],
      serviceId: [bookingDetails?.service_id],
      user_id: bookingDetails?.user_id,
      booking_date: bookingDetails?.booking_start_date,
     
    });
  }, [open, bookingDetails]);

  const onSubmit = (data: FormValues) => {
    setLoading(true);
    const constructedBody = constructChangeDoctorPayload(
      bookingDetails,
      data.doctor_id,
      appointmentBookingCommentEditStatusMapping,
    );

    updateBooking(constructedBody, bookingDetails?.booking_id).then(({ status }) => {
      setLoading(false);
      if (status) {
        setOpen(false);
        if (onSuccess) {
          onSuccess();
        }
      }
    });
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={() => !loading && setOpen(false)}>
        <div className="flex min-h-screen items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-8">
          <Dialog.Panel
            className="w-full max-w-xl rounded-3xl bg-white shadow-2xl overflow-visible"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#F9FAFB] px-6 py-4 flex items-center justify-between border-b border-gray-100 rounded-t-3xl">
              <div className="flex items-center gap-2.5">
                <div className="p-2 bg-black rounded-xl">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-gray-900 leading-tight">Change Doctor</h2>
                  <p className="text-[11px] text-gray-500 font-medium tracking-wider mt-0.5">Reassign Provider</p>
                </div>
              </div>
              <button
                type="button"
                className="p-1.5 rounded-full hover:bg-gray-200 transition-colors bg-gray-100"
                disabled={loading}
                onClick={() => setOpen(false)}
              >
                <XMarkIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>

            <div className="p-5 space-y-6">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100 font-primary">
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-500 tracking-wider">Customer Name</p>
                  <p className="text-xs font-medium text-gray-900">{bookingDetails?.user_details?.name || "NA"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-500 tracking-wider">Pet Details</p>
                  <p className="text-xs font-medium text-gray-900">{bookingDetails?.pet_details?.name || "NA"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-500 tracking-wider">Clinic Name</p>
                  <p className="text-xs font-medium text-gray-900">{bookingDetails?.clinic_details?.name || "NA"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-500 tracking-wider">Service</p>
                  <p className="text-xs font-medium text-gray-900">{bookingDetails?.service_details?.name || "NA"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-500 tracking-wider">Current Doctor</p>
                  <p className="text-xs font-medium text-gray-900">{bookingDetails?.staff_details?.name || "NA"}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[11px] font-bold text-gray-700 tracking-wider flex items-center gap-1.5 ml-1">
                    Select New Doctor <span className="text-red-500">*</span>
                  </label>
                  <SearchableSelectMenu
                    errorMessages={[{ message: "Doctor is required", type: "required" }]}
                    fieldError={formHook.formState.errors?.doctor_id}
                    register={formHook.register("doctor_id", { required: true })}
                    placeholder="Search and select doctor..."
                    showDropdownIcon
                    showDropdownWhenNoData
                    containerClassName="w-full"
                    listZIndex="z-9999 "
                    selectItems={providerList}
                    onSelectItem={(item) => {
                      if (!item) return;
                      formHook.setValue("doctor_id", item.id, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }}
                  />
                </div>

                <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-50 rounded-lg">
                  <Info className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                  <p className="text-[10px] text-amber-700 font-medium italic">
                    This action will reschedule the booking to the newly selected provider.
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t border-gray-100 rounded-b-3xl">
              <button
                type="button"
                disabled={loading}
                onClick={() => setOpen(false)}
                className="px-5 py-2.5 text-xs font-bold text-gray-600 hover:text-gray-900 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={loading || !isValid}
                onClick={formHook.handleSubmit(onSubmit)}
                className={clsx(
                  "rounded-xl px-8 py-2.5 text-xs font-bold text-white shadow-sm transition-all active:scale-95",
                  !loading && isValid ? "bg-black hover:bg-gray-800" : "bg-gray-400 cursor-not-allowed",
                )}
              >
                {loading ? "Updating..." : "Update Doctor"}
              </button>
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ChangeDoctorModal;
